import { useEffect, useRef, useState } from "react";
import { Play, Pause, Music2, VolumeX } from "lucide-react";
import { Slider } from "@/components/ui/slider";

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { TAALS, getTaal } from "@/lib/taals";
import { TANPURA_KEYS } from "@/lib/notes";
import {
    getCtx,
    playBol,
    preloadTablaSamples,
    startTanpura,
    stopSource,
} from "@/lib/audio";

// Reference key of the recorded tabla bol samples (approx. C dayan).
// We pitch-shift all bols by (scale - reference) semitones to match
// the selected Sa, the way an electronic tabla machine tunes its dayan.
const TABLA_REF_KEY_IDX = 0; // C

export default function Taals() {
    const [taalId, setTaalId] = useState("teentaal");
    const [bpm, setBpm] = useState(80);
    const [playing, setPlaying] = useState(false);
    const [currentBeat, setCurrentBeat] = useState(-1);
    const [scaleIdx, setScaleIdx] = useState(7); // default G
    const [tanpuraOn, setTanpuraOn] = useState(true);

    const sched = useRef({ nextTime: 0, beat: 0, intervalId: null });
    const tanpuraSrcRef = useRef(null);
    const tanpuraReqIdRef = useRef(0);
    const taal = getTaal(taalId);
    const scaleSemitones = scaleIdx - TABLA_REF_KEY_IDX;

    // Preload all tabla samples once
    useEffect(() => {
        preloadTablaSamples().catch(() => {});
    }, []);

    // Tabla scheduler
    useEffect(() => {
        if (!playing) {
            if (sched.current.intervalId) clearInterval(sched.current.intervalId);
            sched.current.intervalId = null;
            setCurrentBeat(-1);
            return;
        }
        const ctx = getCtx();
        const spb = 60 / bpm;
        sched.current.nextTime = ctx.currentTime + 0.08;
        sched.current.beat = 0;

        const scheduler = () => {
            const lookahead = ctx.currentTime + 0.25;
            while (sched.current.nextTime < lookahead) {
                const idx = sched.current.beat % taal.matras;
                const b = taal.beats[idx];
                const accent = b.type === "sam" || b.type === "tali";
                playBol(b.bol, sched.current.nextTime, accent, scaleSemitones);
                const at = (sched.current.nextTime - ctx.currentTime) * 1000;
                setTimeout(() => setCurrentBeat(idx), Math.max(0, at));
                sched.current.beat += 1;
                sched.current.nextTime += spb;
            }
        };
        scheduler();
        const id = setInterval(scheduler, 80);
        sched.current.intervalId = id;
        return () => clearInterval(id);
    }, [playing, taalId, bpm, taal, scaleSemitones]);

    // Tanpura accompaniment: start/stop/key change
    useEffect(() => {
        const reqId = ++tanpuraReqIdRef.current;
        const shouldPlay = playing && tanpuraOn;
        // Always stop any previous tanpura first
        if (tanpuraSrcRef.current) {
            stopSource(tanpuraSrcRef.current);
            tanpuraSrcRef.current = null;
        }
        if (!shouldPlay) return;
        const note = TANPURA_KEYS[scaleIdx].note;
        startTanpura(note, 0.55).then((src) => {
            if (tanpuraReqIdRef.current !== reqId) {
                stopSource(src);
                return;
            }
            tanpuraSrcRef.current = src;
        }).catch(() => {});
        return () => {
            if (tanpuraSrcRef.current) {
                stopSource(tanpuraSrcRef.current);
                tanpuraSrcRef.current = null;
            }
        };
    }, [playing, tanpuraOn, scaleIdx]);

    // Group beats into vibhags for rendering
    const vibhagGroups = [];
    let p = 0;
    taal.vibhag.forEach((vlen) => {
        vibhagGroups.push(taal.beats.slice(p, p + vlen).map((b, i) => ({ ...b, _idx: p + i })));
        p += vlen;
    });

    return (
        <div data-testid="taals-page" className="space-y-10">
            <header>
                <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">
                    Tool · 02
                </div>
                <h1 className="font-sans font-extrabold text-5xl lg:text-6xl tracking-tight">
                    Taals
                </h1>
                <p className="text-muted-foreground mt-3 max-w-2xl">
                    {taal.matras} matras · {taal.vibhag.join(" + ")} · Pick a scale to tune both the tabla dayan and the tanpura drone — just like an electronic Sangat.
                </p>
            </header>

            {/* Select Taal */}

<div className="rounded-lg border border-border bg-card p-6">

    <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">

        Select Taal

    </div>

    <Select

        value={taalId}

        onValueChange={(value) => {

            setPlaying(false);

            setTaalId(value);

        }}

    >

        <SelectTrigger className="w-full h-14 text-lg">

            <SelectValue />

        </SelectTrigger>

        <SelectContent>

            {TAALS.map((taal) => (

                <SelectItem

                    key={taal.id}

                    value={taal.id}

                >

                    {taal.name} ({taal.matras} Beats)

                </SelectItem>

            ))}

        </SelectContent>

    </Select>

</div>

            {/* Select Scale */}

<div className="rounded-lg border border-border bg-card p-6 mt-6">

    <div className="flex items-center justify-between mb-4">

        <div>

            <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-3">

                Select Scale

            </div>

        </div>

        <button

            onClick={() => setTanpuraOn(v => !v)}

            className={`

                px-4

                h-10

                rounded-md

                border

                text-sm

                transition-all

                ${tanpuraOn

                    ? "bg-primary text-primary-foreground"

                    : "bg-secondary"

                }

            `}

        >

            {tanpuraOn

                ? "Tanpura ON"

                : "Tanpura OFF"}

        </button>

    </div>

    <Select

        value={String(scaleIdx)}

        onValueChange={(value) =>

            setScaleIdx(Number(value))

        }

    >

        <SelectTrigger className="w-full h-14 text-lg">

            <SelectValue />

        </SelectTrigger>

        <SelectContent>

            {TANPURA_KEYS.map((key, index) => (

                <SelectItem

                    key={key.note}

                    value={String(index)}

                >

                    {key.label}

                </SelectItem>

            ))}

        </SelectContent>

    </Select>

    <p className="text-xs text-muted-foreground mt-4">

        Tabla and Tanpura will both be tuned to

        <span className="font-bold">

            {" "} {TANPURA_KEYS[scaleIdx].note}

        </span>

    </p>

</div>

            {/* Beat grid */}
            <div className="rounded-lg border border-border bg-card p-6 lg:p-10">
                <div className="flex items-baseline justify-between mb-6">
                    <div>
                        <div className="font-bold text-3xl">{taal.name}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                            {taal.description}
                        </div>
                    </div>
                    <div className="hidden sm:flex font-mono text-xs uppercase tracking-widest text-muted-foreground gap-4">
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-primary" />
                            Sam
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-accent" />
                            Tali
                        </span>
                        <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-muted-foreground" />
                            Khali
                        </span>
                    </div>
                </div>

                <div className="flex flex-wrap gap-x-6 gap-y-4">
                    {vibhagGroups.map((group, gi) => (
                        <div key={gi} className="flex gap-2">
                            {group.map((b) => {
                                const active = b._idx === currentBeat;
                                const typeColor =
                                    b.type === "sam"
                                        ? "border-primary text-primary"
                                        : b.type === "tali"
                                          ? "border-accent text-accent"
                                          : b.type === "khali"
                                            ? "border-muted-foreground text-muted-foreground"
                                            : "border-border text-foreground";
                                return (
                                    <div
                                        key={b._idx}
                                        data-testid={`taal-beat-${b._idx}`}
                                        className={`w-16 h-20 rounded-md border-2 ${typeColor} flex flex-col items-center justify-center transition-all ${
                                            active
                                                ? "bg-primary/10 scale-110 shadow-md"
                                                : ""
                                        }`}
                                    >
                                        <div className="font-mono text-[10px] uppercase opacity-70">
                                            {b._idx + 1}
                                        </div>
                                        <div className="font-bold text-lg leading-tight mt-1">
                                            {b.bol}
                                        </div>
                                        {b.type && (
                                            <div className="font-mono text-[9px] uppercase mt-1 opacity-80">
                                                {b.type === "sam"
                                                    ? "×"
                                                    : b.type === "khali"
                                                      ? "0"
                                                      : "•"}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>

            {/* Controls */}
            <div className="grid sm:grid-cols-2 gap-6">
                <div className="rounded-lg border border-border bg-card p-6">
                    <div className="flex items-center justify-between mb-3">
                        <label className="text-xs uppercase tracking-[0.22em] text-muted-foreground">
                            Tempo
                        </label>
                        <span
                            data-testid="taals-bpm-value"
                            className="font-mono text-2xl"
                        >
                            {bpm} <span className="text-xs">BPM</span>
                        </span>
                    </div>
                    <Slider
                        data-testid="taals-bpm-slider"
                        value={[bpm]}
                        onValueChange={([v]) => setBpm(v)}
                        min={40}
                        max={240}
                        step={1}
                    />
                </div>
                <button
                    data-testid="taals-play-button"
                    onClick={() => setPlaying((p) => !p)}
                    className={`h-full min-h-[88px] rounded-lg border font-medium text-base flex items-center justify-center gap-2 transition-all ${
                        playing
                            ? "bg-foreground text-background border-foreground"
                            : "bg-primary text-primary-foreground border-primary hover:-translate-y-0.5"
                    }`}
                >
                    {playing ? (
                        <>
                            <Pause size={20} /> Stop
                        </>
                    ) : (
                        <>
                            <Play size={20} /> Play {taal.name}
                            {tanpuraOn ? ` + Tanpura ${TANPURA_KEYS[scaleIdx].note}` : ""}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}
